import { Component, Inject, Injectable } from '@angular/core';
import { MAT_SNACK_BAR_DATA, MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { AnyType } from '@thehubz/shared/model';
import { BehaviorSubject, Observable } from 'rxjs';

export type MessageType = 'success' | 'info' | 'warning' | 'failure';

export interface SnackBarMessage {
  type: MessageType;
  title?: string;
  message: string | string[];
  action?: string;
  counter$?: Observable<number>;

  /**
   * Duration in milliseconds
   */
  duration?: number;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly msgQueue: SnackBarMessage[] = [];
  private currentMsg?: SnackBarMessage;
  private messageCounter = new BehaviorSubject<number>(0);

  isInstanceVisible = false;

  constructor(private snackBar: MatSnackBar) {}

  // Adding snackbar data to msgQueue array
  addNotification(data: SnackBarMessage): MatSnackBarRef<AnyType> | undefined {
    if (typeof data.message === 'string' && this.filterMessage(data.message)) {
      console.log('Message not shown', data);
      return;
    }

    if (this.containsMessage(data)) {
      return;
    }

    data.counter$ = this.messageCounter.asObservable();
    this.msgQueue.push(data);
    this.messageCounter.next(this.msgQueue.length);
    if (!this.isInstanceVisible) {
      return this.showNext();
    } else {
      return;
    }
  }

  private containsMessage(newMsg: SnackBarMessage) {
    if (this.currentMsg?.message === newMsg.message) {
      return true;
    }
    for (const msg of this.msgQueue) {
      if (msg.message === newMsg.message) {
        return true;
      }
    }
    return false;
  }

  /* When user clicks on dismiss or when snackbar closes next snackbar will displayed */
  /* Checking in msgQueue array for snackbar data, displaying the snackbar and then after
  snackbar is closed, removing the shown data from msgQueue array and displaying the next data */
  private showNext(): MatSnackBarRef<NotificationComponent> | undefined {
    if (this.msgQueue.length === 0) {
      return;
    }
    this.currentMsg = this.msgQueue.shift();
    this.messageCounter.next(this.msgQueue.length);
    if (this.currentMsg) {
      this.isInstanceVisible = true;
      const snackBarRef = this.openSnackBar(this.currentMsg);
      snackBarRef.afterDismissed().subscribe(() => {
        this.currentMsg = undefined;
        this.isInstanceVisible = false;
        this.showNext();
      });
      return snackBarRef;
    } else {
      return;
    }
  }

  // Showing snackbar based on type
  private openSnackBar(message: SnackBarMessage) {
    switch (message.type) {
      case 'success':
        return this.openSuccessSnackBar(message);
      case 'info':
        return this.openInfoSnackBar(message);
      case 'warning':
        return this.openWarningSnackBar(message);
      case 'failure':
        return this.openFailureSnackBar(message);
      default:
        return this.openInfoSnackBar(message);
    }
  }

  // Snackbar that opens with success background
  private openSuccessSnackBar(data: SnackBarMessage) {
    return this.snackBar.openFromComponent(NotificationComponent, {
      duration: data.duration || 4000,
      data,
      verticalPosition: 'top',
      horizontalPosition: 'end',
      panelClass: ['success-snackbar'],
    });
  }

  //Snackbar that opens with failure background
  private openFailureSnackBar(data: SnackBarMessage) {
    // patch some messages to be more user friendly
    if (data.message.includes('<title>502')) {
      data.message = 'Server is not reachable. Please try again - refresh the page.';
    }
    return this.snackBar.openFromComponent(NotificationComponent, {
      duration: data.duration,
      data,
      verticalPosition: 'top',
      horizontalPosition: 'end',
      panelClass: ['error-snackbar'],
    });
  }

  //Snackbar that opens with warning background
  private openWarningSnackBar(data: SnackBarMessage) {
    return this.snackBar.openFromComponent(NotificationComponent, {
      duration: data.duration || 8000,
      data,
      verticalPosition: 'top',
      horizontalPosition: 'end',
      panelClass: ['warning-snackbar'],
    });
  }

  private filterMessage(message: string) {
    // if we get ugly text from the server, we don't want to show it
    return message.startsWith('<!DOCTYPE HTML');
  }

  //Snackbar that opens with info background
  private openInfoSnackBar(data: SnackBarMessage) {
    return this.snackBar.openFromComponent(NotificationComponent, {
      duration: data.duration || 4000,
      data: { title: data.title, message: data.message },
      verticalPosition: 'top',
      horizontalPosition: 'end',
      panelClass: ['info-snackbar'],
    });
  }
}

@Component({
  template: `
    <div class="notification" (click)="snackBarRef.dismiss()">
      <div>
        <div class="notification-title">
          {{ data.title }}
        </div>
        <div>{{ data.message }}&nbsp;</div>
      </div>
      <div class="notification-action">
        <button (click)="snackBarRef.dismiss()">{{ data.action || 'x' }}</button>
        <span class="notification-count" *ngIf="data.counter$ | async; let count">[ {{ count + 1 }} ]</span>
      </div>
    </div>
  `,
  styles: [
    `
      .notification {
        display: flex;
        justify-content: space-between;
        cursor: pointer;
      }
      .notification-title {
        font-weight: bold;
      }
      .notification-action {
        @apply flex flex-col;
        min-width: 30px;
      }
      .notification-count {
        margin-top: 3px;
      }
    `,
  ],
})
export class NotificationComponent {
  constructor(
    protected snackBarRef: MatSnackBarRef<NotificationComponent>,
    @Inject(MAT_SNACK_BAR_DATA) protected data: SnackBarMessage,
  ) {}
}
